/**
 * One rule, for every screen, for a participant's status ("active" / "inactive") and what
 * needs attention. Uses the server's per-device view (`devices`) when present; with an older
 * server that does not send it, keeps the previous behavior.
 * See docs/specs/participant-devices.md.
 */
import { DeviceTypeParticipant, deviceAttention, participantHasFitbit, participantIsActive } from './deviceType';

export type StatusParticipant = DeviceTypeParticipant & {
  authorizationStatus?: boolean;
  verified?: boolean;
  fitbitTokens?: { accessToken?: string; expiresAt?: string | number } | null;
  historicalExtraction?: { status?: string; consecutiveNoDataDays?: number } | null;
};

const expired = (value: string | number) => new Date(value) <= new Date();

/** Active when at least one device is connected. */
export function participantStatus(participant: StatusParticipant): 'active' | 'inactive' {
  const active = participantIsActive(participant);
  if (active !== null) return active ? 'active' : 'inactive';
  // Older server without devices.
  if (participant.healthConnection?.provider === 'google_health') {
    return participantHasFitbit(participant) || participant.healthkit?.authorized ? 'active' : 'inactive';
  }
  if (!participant.authorizationStatus) return 'inactive';
  if (participant.fitbitTokens?.accessToken && participant.fitbitTokens.expiresAt) {
    return expired(participant.fitbitTokens.expiresAt) ? 'inactive' : 'active';
  }
  return 'active';
}

/** What needs attention, or '' when nothing does. */
export function attentionReason(participant: StatusParticipant): string {
  if (participant.fitbitCollectionEnrollment || participant.ultrahumanCollectionEnrollment) {
    const reason = deviceAttention(participant);
    if (reason !== null) return reason;
    // Older server without devices.
    if (participant.fitbitCollectionEnrollment) {
      if (participant.healthConnection?.status === 'action_required') return 'Fitbit reconnection required';
      if (participant.healthConnection?.status === 'disabled') return 'Fitbit connection disabled';
      return participant.authorizationStatus ? '' : 'Enrollment not complete';
    }
    return participant.ultrahumanEnrollmentStatus === 'completed' ? '' : 'Enrollment not complete';
  }
  // Older app, HealthKit and direct-Fitbit participants.
  if (!participant.authorizationStatus) return 'Not authorized';
  if (participant.fitbitTokens?.accessToken && participant.fitbitTokens.expiresAt && expired(participant.fitbitTokens.expiresAt)) {
    return 'Token expired';
  }
  if (!participant.fitbitTokens?.accessToken && !participant.healthkit?.authorized) return 'No data source';
  if (!participant.verified) return 'Not verified';
  if (participant.historicalExtraction?.status === 'in_progress' &&
    (participant.historicalExtraction.consecutiveNoDataDays || 0) > 7) return 'Extraction stalled';
  return '';
}

export function needsAttention(participant: StatusParticipant): boolean {
  return attentionReason(participant) !== '';
}
